import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr, fmt, fmtNum, fileUrl, API_BASE } from "@/lib/api";
import { Button, Card, StatusPill } from "@/components/kit";
import { ArrowLeft, Link2, ExternalLink, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value, strong }) => (
  <div className={`flex items-center justify-between py-1.5 ${strong ? "border-t border-slate-700 pt-2.5 mt-1" : ""}`}>
    <span className={strong ? "font-display font-bold text-white" : "text-sm text-slate-400"}>{label}</span>
    <span className={strong ? "font-display text-lg font-bold text-amber-400" : "text-sm text-slate-200"}>{value}</span>
  </div>
);

export default function SignoffDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  const load = () => api.get(`/signoffs/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const emailLink = async () => { try { const { data } = await api.post(`/signoffs/${id}/email`); toast.success("Mejlat till " + data.to); load(); } catch (e) { toast.error(apiErr(e)); } };
  const markInvoiced = async () => { try { await api.put(`/signoffs/${id}/status?status=invoiced`); toast.success("Markerad som fakturerad"); load(); } catch (e) { toast.error(apiErr(e)); } };

  if (!d) return <div className="text-slate-400">Laddar…</div>;
  const { signoff: s, project: p, summary, vat, total, entries, company } = d;
  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/sign/${s.token}`); toast.success("Länk kopierad"); };

  return (
    <div className="rise">
      <button onClick={() => nav("/signoffs")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Tillbaka</button>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-extrabold text-white sm:text-3xl">{s.title}</h1>
            <StatusPill status={s.status} />
          </div>
          <p className="mt-1 text-sm text-slate-400">{p?.name} · {s.date_from} – {s.date_to}{s.invoice_number ? ` · Faktura ${s.invoice_number} (${s.invoice_date})` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="email-signoff-button" onClick={emailLink}><Mail size={16} /> Mejla länk</Button>
          <Button variant="outline" onClick={copyLink}><Link2 size={16} /> Kopiera länk</Button>
          {s.status === "signed" && (
            <Button variant="primary" data-testid="mark-invoiced-button" onClick={markInvoiced}><CheckCircle2 size={16} /> Markera fakturerad</Button>
          )}
          <a href={`${API_BASE}/signoffs/${id}/pdf`} target="_blank" rel="noreferrer"><Button variant="dark"><ExternalLink size={16} /> Fakturaunderlag PDF</Button></a>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="overflow-hidden">
            <div className="border-b border-slate-800 px-5 py-4"><h2 className="font-display font-bold text-white">Rapporter ({entries.length})</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr className="border-b border-slate-800">
                    <th className="px-5 py-3">Datum</th><th className="px-5 py-3">Anställd</th>
                    <th className="px-5 py-3 text-right">Tim</th><th className="px-5 py-3 text-right">Maskin</th>
                    <th className="px-5 py-3 text-right">Material</th><th className="px-5 py-3 text-right">Belopp</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b border-slate-800/60">
                      <td className="px-5 py-3 font-mono text-slate-300">{e.date}</td>
                      <td className="px-5 py-3 text-slate-300">{e.employee_name}</td>
                      <td className="px-5 py-3 text-right text-slate-300">{fmtNum(e.work_hours)}</td>
                      <td className="px-5 py-3 text-right text-slate-300">{fmt(e.cost.machine)}</td>
                      <td className="px-5 py-3 text-right text-slate-300">{fmt(e.cost.material)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-white">{fmt(e.cost.total)}</td>
                    </tr>
                  ))}
                  {entries.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">Inga rapporter i perioden.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-5">
            <h2 className="mb-2 font-display font-bold text-white">Kostnadssammanställning</h2>
            <p className="mb-3 text-xs text-slate-500">Beställaren ser {d.show_amounts ? <span className="text-amber-400">belopp</span> : "endast tid & materialmängd"}.</p>
            <Row label={`Arbete (${fmtNum(summary.work_hours)} h)`} value={fmt(summary.labor)} />
            <Row label={`Maskin (${fmtNum(summary.machine_hours)} h)`} value={fmt(summary.machine)} />
            <Row label="Material" value={fmt(summary.material)} />
            <Row label="Delsumma" value={fmt(summary.subtotal)} />
            <Row label={`Moms (${fmtNum(company.vat_rate)}%)`} value={fmt(vat)} />
            <Row label="Att betala" value={fmt(total)} strong />
          </Card>

          <Card className="p-5">
            <h2 className="mb-3 font-display font-bold text-white">Signatur</h2>
            {(s.status === "signed" || s.status === "invoiced") ? (
              <div>
                <div className="mb-3 inline-flex items-center gap-1.5 text-sm text-emerald-400"><CheckCircle2 size={16} /> Godkänt</div>
                {s.signature_type === "draw" ? (
                  <img src={fileUrl(s.signature_data)} alt="signatur" className="rounded-lg border border-slate-700 bg-white p-2" />
                ) : (
                  <div className="rounded-lg border border-slate-700 bg-white px-4 py-3 text-2xl text-slate-900" style={{ fontFamily: "'Outfit'" }}>{s.signature_data}</div>
                )}
                <p className="mt-2 text-sm text-slate-400">{s.contractor_name} · {String(s.signed_at).slice(0, 10)}</p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Väntar på beställarens signatur. Dela attestlänken ovan.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
